import React from "react";
import { useParams, Navigate } from "react-router-dom";
import useFetch from "@/lib/useFetch";
import { MetricCard, GlassCard, PageTitle, StatusBadge, EmptyState } from "@/components/lux";
import { money } from "@/lib/api";
import { TrendingUp, Users, Network, Wallet, Hash } from "lucide-react";

const KINDS = {
  direct: { label: "Referral Bonus", type: "direct_referral", icon: Users, desc: "Referral bonuses earned from your direct team's activations." },
  binary: { label: "Performance IB", type: "binary_matching", icon: Network, desc: "Matching bonus on paired left & right business volume." },
  roi: { label: "ROI Income", type: "self_performance", icon: TrendingUp, desc: "Daily self-performance ROI credited on your active package." },
};

export default function IncomeDetail() {
  const { kind } = useParams();
  const meta = KINDS[kind];
  const { data, loading } = useFetch(`/user/transactions?type=${meta?.type}`, [kind]);
  const { data: inc } = useFetch("/user/income");
  if (!meta) return <Navigate to="/app/income/direct" replace />;
  const Icon = meta.icon;
  const ledger = inc?.ledgers?.[meta.type] || { total: 0, count: 0 };
  const txs = data?.transactions || [];
  return (
    <div className="space-y-6" data-testid={`income-detail-${kind}`}>
      <PageTitle title={meta.label} subtitle={meta.desc} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricCard label={`Total ${meta.label}`} value={`$${money(ledger.total)}`} icon={Icon} accent />
        <MetricCard label="Entries" value={ledger.count} icon={Hash} />
        <MetricCard label="Available Wallet" value={`$${money(inc?.balances?.available || 0)}`} icon={Wallet} />
      </div>
      <GlassCard className="p-6" data-testid={`income-${kind}-table`}>
        <h3 className="mb-4 font-semibold text-white">History</h3>
        {loading ? <div className="skeleton h-40" /> : txs.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[var(--text-dim)]"><tr className="border-b border-white/10">
                <th className="py-3">TX ID</th><th className="py-3">Description</th><th className="py-3">Status</th><th className="py-3">Date</th><th className="py-3 text-right">Amount</th></tr></thead>
              <tbody>
                {txs.map((t, i) => (
                  <tr key={i} className="row-hover border-b border-white/5">
                    <td className="py-3 text-white">{t.tx_id}</td>
                    <td className="py-3 text-[var(--text-dim)]">{t.description}</td>
                    <td className="py-3"><StatusBadge status={t.status} /></td>
                    <td className="py-3 text-[var(--text-dim)]">{(t.created_at || "").slice(0, 10)}</td>
                    <td className="py-3 text-right gold-text font-medium">+${money(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState icon={Icon} title={`No ${meta.label.toLowerCase()} yet`} />}
      </GlassCard>
    </div>
  );
}
